import { useEffect, useState } from "react";
import { Bell, Copy, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { nudgeApi } from "@/lib/api";

// Format seconds → "1h 22m" or "12m" or "44s"
function formatAgo(secs) {
  if (secs == null || secs < 0) return "";
  if (secs < 60) return `${Math.floor(secs)}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m ago` : `${h}h ago`;
}

/**
 * NudgeDialog — opens when the producer clicks "Nudge" on an at-risk row.
 *
 * Behavior:
 *   • Pulls pre-fill copy from GET /api/plan-tasks/{id}/nudge/preview
 *   • Subject + body are fully editable
 *   • Send Nudge → POST /api/plan-tasks/{id}/nudge
 *   • Copy → clipboard (plain text body, includes deep link), no email
 *   • 4h throttle is server-enforced; modal warns + lets producer override
 */
export default function NudgeDialog({ taskId, open, onOpenChange, onSent }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    nudgeApi
      .preview(taskId)
      .then((d) => {
        if (cancelled) return;
        setPreview(d);
        setSubject(d.default_subject || "");
        setBody(d.default_body || "");
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || "Could not load nudge preview";
        toast.error(typeof msg === "string" ? msg : "Could not load nudge preview");
        onOpenChange?.(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, taskId, onOpenChange]);

  const handleSend = async (force = false) => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message are both required");
      return;
    }
    setSending(true);
    try {
      const r = await nudgeApi.send(taskId, { subject, body, force });
      toast.success(`Nudge sent to ${r.recipient_email}`);
      onSent?.(r);
      onOpenChange?.(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && typeof detail === "object") {
        const secs = detail.last_nudged_seconds_ago;
        toast.warning(
          `This task was nudged ${formatAgo(secs)} — click "Send anyway" to override.`,
          { duration: 6000 },
        );
        // Refresh preview so the warning banner re-renders
        setPreview((p) => p && { ...p, in_throttle_window: true, last_nudged_seconds_ago: secs });
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to send nudge");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    const full = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable — select the text manually.");
    }
  };

  // Recompute the throttle banner visibility each render so it disappears
  // when the producer hits Send and the API returns success.
  const inThrottle = preview?.in_throttle_window;
  const lastAgo = preview?.last_nudged_seconds_ago;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        data-testid="nudge-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            <Bell className="w-4 h-4 text-amber-400" />
            Send a nudge
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-zinc-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : preview ? (
          <div className="space-y-4">
            {/* Recipient block */}
            <div
              data-testid="nudge-recipient"
              className="rounded-sm border border-zinc-800 bg-zinc-950/60 px-3 py-2 flex items-baseline justify-between gap-3"
            >
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono mb-0.5">
                  To
                </div>
                <div className="text-sm text-zinc-200">
                  {preview.recipient.name || "—"}{" "}
                  <span className="text-zinc-500 text-xs font-mono">
                    {preview.recipient.email}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-mono mb-0.5">
                  Re
                </div>
                <div className="text-xs text-zinc-300 truncate max-w-[200px]">
                  {preview.task_name}
                </div>
              </div>
            </div>

            {/* Throttle warning banner */}
            {inThrottle && (
              <div
                data-testid="nudge-throttle-warning"
                className="rounded-sm border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-200 flex items-start gap-2"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  Heads-up: this task was nudged{" "}
                  <strong className="font-mono">{formatAgo(lastAgo)}</strong>.
                  You can still send, but the assignee may have already seen
                  it. Click <em>Send anyway</em> below to override.
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-zinc-500 font-mono mb-1">
                Subject
              </label>
              <Input
                data-testid="nudge-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={140}
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-zinc-500 font-mono mb-1">
                Message
              </label>
              <Textarea
                data-testid="nudge-body-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                maxLength={4000}
                className="font-mono text-xs leading-relaxed"
              />
              <div className="text-[10px] text-zinc-600 font-mono mt-1">
                Replies will go to your email ({preview.recipient.email ? "via Resend" : ""}). Edit freely before sending.
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex-row sm:justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="nudge-copy-btn"
            onClick={handleCopy}
            disabled={loading || !body}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" />
            Copy
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange?.(false)}
              disabled={sending}
              data-testid="nudge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="nudge-send-btn"
              onClick={() => handleSend(inThrottle === true)}
              disabled={sending || loading || preview?.is_self_nudge}
              className={
                inThrottle
                  ? "bg-orange-500 hover:bg-orange-400 text-zinc-950"
                  : "bg-amber-500 hover:bg-amber-400 text-zinc-950"
              }
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5 mr-1.5" />
              )}
              {inThrottle ? "Send anyway" : "Send Nudge"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
